import { ComponentProps, useMemo } from "react";

import { Clickable } from "@design-system/Clickable/Clickable";
import { Icon } from "@design-system/Icon/Icon";
import { Text } from "@design-system/Text/Text";
import { Tooltip } from "@design-system/Tooltip";

import classes from "./IconButton.module.css";

type Color =
  | "textDark"
  | "textLight"
  | "textSecondary"
  | "white"
  | "primary"
  | "danger"
  | "inherit";

type Props = {
  icon: ComponentProps<typeof Icon>["icon"];
  onClick?: ComponentProps<typeof Clickable>["onClick"];
  onMouseDown?: ComponentProps<typeof Clickable>["onMouseDown"];
  size?: ComponentProps<typeof Icon>["size"];
  bubbleCount?: number;
  showBubble?: boolean;
  color?: Color;
  outline?: ComponentProps<typeof Clickable>["outline"];
  tooltip?: string;
  disabled?: boolean;
};

export const IconButton = ({
  icon,
  onClick,
  onMouseDown,
  size = "lg",
  bubbleCount,
  showBubble,
  color = "textSecondary",
  outline = "sm",
  tooltip,
  disabled,
}: Props) => {
  const bubbletext = useMemo(() => {
    if (bubbleCount && bubbleCount > 9) {
      return "9+";
    }
    return bubbleCount?.toString() || "";
  }, [bubbleCount]);

  const shouldShowBubble =
    showBubble || (bubbleCount !== undefined && bubbleCount > 0);

  const buttonElement = (
    <Clickable
      onClick={onClick}
      onMouseDown={onMouseDown}
      outline={outline}
      disabled={disabled}
    >
      <div className={classes.icon}>
        <Text color={color}>
          <Icon
            size={size}
            icon={icon}
            className={icon === "faArrowsSpin" ? classes.spinning : undefined}
          />
        </Text>
        {shouldShowBubble ? (
          <div
            className={
              bubbletext
                ? classes.bubbleContainer
                : classes.simpleBubbleContainer
            }
          >
            {bubbletext ? <span>{bubbletext}</span> : null}
          </div>
        ) : null}
      </div>
    </Clickable>
  );

  if (tooltip) {
    return <Tooltip content={tooltip}>{buttonElement}</Tooltip>;
  }

  return buttonElement;
};
